import i18next from 'i18next'
import { sentenceFormat } from 'utils/string'

import {
  ActionKeyType,
  AutoGenerateMessage,
  AutoGenerateVariant,
  AutoGenerateVariantConfig,
  VariantConfig,
} from './types'

export enum Actions {
  add = 'add',
  abort = 'abort',
  activate = 'activate',
  approve = 'approve',
  assign = 'assign',
  cancel = 'cancel',
  change = 'change',
  complete = 'complete',
  copy = 'copy',
  create = 'create',
  deactivate = 'deactivate',
  decline = 'decline',
  delete = 'delete',
  disable = 'disable',
  dismiss = 'dismiss',
  discard = 'discard',
  download = 'download',
  enable = 'enable',
  end = 'end',
  export = 'export',
  generate = 'generate',
  import = 'import',
  load = 'load',
  markInvalid = 'markInvalid',
  markLost = 'markLost',
  markPrimary = 'markPrimary',
  merge = 'merge',
  publish = 'publish',
  reactivate = 'reactivate',
  reassign = 'reassign',
  recycle = 'recycle',
  reject = 'reject',
  remove = 'remove',
  reopen = 'reopen',
  reschedule = 'reschedule',
  resend = 'resend',
  resolve = 'resolve',
  restore = 'restore',
  save = 'save',
  schedule = 'schedule',
  select = 'select',
  send = 'send',
  sendByEmail = 'sendByEmail',
  sendToCustomer = 'sendToCustomer',
  setDefault = 'setDefault',
  start = 'start',
  unassign = 'unassign',
  update = 'update',
  upload = 'upload',
  pause = 'pause',
  resume = 'resume',
}

const ACTION_VERBS: { [key in ActionKeyType]: { [key in AutoGenerateVariant]: string } } = Object.freeze({
  abort: {
    error: 'abort',
    success: 'aborted',
  },
  activate: {
    error: 'activate',
    success: 'activated',
  },
  add: {
    error: 'add',
    success: 'added_lbl',
  },
  approve: {
    error: 'approve',
    success: 'approved',
  },
  assign: {
    error: 'assign',
    success: 'assigned',
  },
  cancel: {
    error: 'cancel',
    success: 'cancelled',
  },
  change: {
    error: 'change',
    success: 'changed',
  },
  complete: {
    error: 'complete',
    success: 'completed',
  },
  copy: {
    error: 'copy',
    success: 'copied',
  },
  create: {
    error: 'create_lbl',
    success: 'created',
  },
  deactivate: {
    error: 'deactivate',
    success: 'deactivated',
  },
  decline: {
    error: 'decline',
    success: 'declined',
  },
  delete: {
    error: 'delete',
    success: 'deleted',
  },
  disable: {
    error: 'disable',
    success: 'disabled',
  },
  dismiss: {
    error: 'dismiss',
    success: 'dismissed',
  },
  discard: {
    error: 'discard',
    success: 'discarded',
  },
  download: {
    error: 'download',
    success: 'downloaded',
  },
  enable: {
    error: 'enable',
    success: 'enabled',
  },
  end: {
    error: 'end',
    success: 'ended',
  },
  export: {
    error: 'export',
    success: 'exported',
  },
  generate: {
    error: 'generate',
    success: 'generated',
  },
  import: {
    error: 'import_lbl',
    success: 'imported',
  },
  load: {
    error: 'load',
    success: 'loaded',
  },
  markInvalid: {
    error: 'mark_invalid',
    success: 'marked_invalid',
  },
  markLost: {
    error: 'mark_lost',
    success: 'marked_lost',
  },
  markPrimary: {
    error: 'mark_primary',
    success: 'marked_primary',
  },
  merge: {
    error: 'merge',
    success: 'merged',
  },
  publish: {
    error: 'publish',
    success: 'published',
  },
  reactivate: {
    error: 'reactivate',
    success: 'reactivated',
  },
  reassign: {
    error: 'reassign',
    success: 'reassigned',
  },
  recycle: {
    error: 'recycle',
    success: 'recycled',
  },
  reject: {
    error: 'reject',
    success: 'rejected',
  },
  remove: {
    error: 'remove',
    success: 'removed',
  },
  reopen: {
    error: 'reopen',
    success: 'reopened',
  },
  reschedule: {
    error: 'reschedule',
    success: 'rescheduled',
  },
  resend: {
    error: 'resend',
    success: 'resent',
  },
  resolve: {
    error: 'resolve',
    success: 'resolved',
  },
  restore: {
    error: 'restore',
    success: 'restored',
  },
  save: {
    error: 'save',
    success: 'saved',
  },
  schedule: {
    error: 'schedule',
    success: 'scheduled',
  },
  send: {
    error: 'send',
    success: 'sent',
  },
  sendByEmail: {
    error: 'send_by_email',
    success: 'sent_by_email',
  },
  select: {
    error: 'select',
    success: 'selected',
  },
  sendToCustomer: {
    error: 'send_to_customer',
    success: 'sent_to_customer',
  },
  setDefault: {
    error: 'set_default',
    success: 'set_default',
  },
  start: {
    error: 'start',
    success: 'started',
  },
  unassign: {
    error: 'unassign',
    success: 'unassigned',
  },
  update: {
    error: 'update_lbl',
    success: 'updated',
  },
  upload: {
    error: 'upload',
    success: 'uploaded',
  },
  pause: {
    error: 'pause',
    success: 'paused',
  },
  resume: {
    error: 'resume',
    success: 'resumed',
  },
})

export const VARIANTS: AutoGenerateVariantConfig & VariantConfig = {
  info: {
    color: 'gray7',
    icon: ['far', 'info-circle'],
  },
  success: {
    color: 'green',
    icon: ['far', 'check-circle'],
    template: {
      plural: 'subject_action_plural_success',
      singular: 'subject_action_singular_success',
    },
  },
  error: {
    color: 'red',
    icon: ['far', 'times-circle'],
    template: {
      plural: 'subject_action_plural_error',
      singular: 'subject_action_singular_error',
    },
  },
  warning: {
    color: 'yellow',
    icon: ['far', 'exclamation-triangle'],
  },
}

// https://www.i18next.com/translation-function/nesting#passing-nesting-to-interpolated
export const getAutoGeneratedMessage = (props: AutoGenerateMessage) => {
  const message = i18next.t(
    (VARIANTS as AutoGenerateVariantConfig)[props.variant].template[props.usePlural ? 'plural' : 'singular'],
    {
      action: `$t(${ACTION_VERBS[props.actionKey][props.variant]})`,
      subject: `$t(${props.subject})`,
    },
  )

  return sentenceFormat(message)
}
